import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack } from '@mui/material';
import * as React from 'react';

import { useDeveloperApi } from './api';
import type { ApiCredential } from './apiKeyTypes';
import { describeError, formatTs } from './components';
import { ShownOnceKey } from './NewApiKeyDialog';

interface Props {
    /** The key to rotate; null closes the dialog. */
    credential: ApiCredential | null;
    onClose: () => void;
    /** Called once the new key exists (the list behind the dialog is read again). */
    onRotated: () => void;
}

/** Developer -> API keys -> Rotate (POST .../rotate/, DSEC D1): a new key with the same settings, shown
 *  once; the current key keeps working until its grace period ends, then it is revoked. */
const RotateApiKeyDialog = ({ credential, onClose, onRotated }: Props) => {
    const api = useDeveloperApi();
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const [result, setResult] = React.useState<{ rawKey: string; oldUntil: string | null } | null>(null);

    const reset = () => {
        setBusy(false);
        setError(null);
        setResult(null);
    };

    const rotate = async () => {
        if (!credential) {
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const rotated = await api.rotateApiCredential(credential.id);
            setResult({ rawKey: rotated.raw_key, oldUntil: rotated.rotated_from?.expires ?? null });
            onRotated();
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={!!credential}
            // While the new key is shown only Done closes it; while it is being made nothing does.
            onClose={result || busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="rotate-api-key-title"
            TransitionProps={{ onExited: reset }}
            // Above the app's side menu (zIndex 10000 in layout/main.js).
            sx={{ zIndex: 10002 }}
        >
            <DialogTitle id="rotate-api-key-title">{result ? 'New API key' : `Rotate "${credential?.name || ''}"?`}</DialogTitle>
            {result ? (
                <>
                    <DialogContent>
                        <ShownOnceKey rawKey={result.rawKey}>
                            <Alert severity="info" data-testid="rotation-grace">
                                {result.oldUntil
                                    ? `The old key keeps working until ${formatTs(result.oldUntil)}, then it is revoked. Switch your integration to the new key before then.`
                                    : 'The old key keeps working for a grace period, then it is revoked. Switch your integration to the new key before then.'}
                            </Alert>
                        </ShownOnceKey>
                    </DialogContent>
                    <DialogActions>
                        <Button variant="contained" onClick={onClose}>Done</Button>
                    </DialogActions>
                </>
            ) : (
                <>
                    <DialogContent>
                        <Stack spacing={2}>
                            <DialogContentText>
                                A new key with the same name, description, flags, allowed IPs, user-agent pattern, expiry and
                                access groups is created now and shown once. The current key keeps working for a grace period
                                (never past its own expiry), then it is revoked.
                            </DialogContentText>
                            {error ? <Alert severity="error">{describeError(error)}</Alert> : null}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button variant="contained" onClick={rotate} disabled={busy} startIcon={busy ? <CircularProgress size={14} /> : undefined}>
                            Rotate
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default RotateApiKeyDialog;
